import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { map } from 'rxjs';
import { AuthService } from '../../../auth/auth.service';
import { NotificationsService } from '../../../services/notifications.service';
import { Authority } from '../../../types/user.type';

interface IRoute {
  path: string;
  name: string;
  icon: string;
}

interface DashboardHeaderNavViewModel {
  routes: IRoute[];
  notificationsPath: string;
}

function buildRoutes({
  authority,
  hasAdminProjectsAccess,
  hasFullAdminOrgAccess,
  canAccessBatteryCatalogAdmin,
  canAccessProductionView,
  canAccessResellerWorkspace,
  isInAdminOrg,
}: {
  authority: Authority;
  hasAdminProjectsAccess: boolean;
  hasFullAdminOrgAccess: boolean;
  canAccessBatteryCatalogAdmin: boolean;
  canAccessProductionView: boolean;
  canAccessResellerWorkspace: boolean;
  isInAdminOrg: boolean;
}): IRoute[] {
  if (authority === 'production' && canAccessProductionView) {
    return [
      {
        path: '/admin/production',
        name: 'Production',
        icon: 'assets/icons/navigation.svg?v=2#stack',
      },
    ];
  }

  if (authority === 'logistics' || authority === 'accounting') {
    return [
      {
        path: '/projects',
        name: 'Projects',
        icon: 'assets/icons/navigation.svg?v=2#projects',
      },
    ];
  }

  const isSupportAdminActor = hasAdminProjectsAccess && !hasFullAdminOrgAccess;

  if (isSupportAdminActor) {
    return [
      {
        path: '/admin/dashboard',
        name: 'Admin',
        icon: 'assets/icons/navigation.svg?v=2#admin',
      },
      {
        path: '/admin/projects',
        name: 'Projects',
        icon: 'assets/icons/navigation.svg?v=2#projects',
      },
      {
        path: '/admin/support',
        name: 'Support',
        icon: 'assets/icons/navigation.svg?v=2#support',
      },
      ...(canAccessProductionView
        ? [
            {
              path: '/admin/production',
              name: 'Production',
              icon: 'assets/icons/navigation.svg?v=2#stack',
            },
          ]
        : []),
      ...(canAccessBatteryCatalogAdmin
        ? [
            {
              path: '/admin/batteries',
              name: 'Batteries',
              icon: 'assets/icons/navigation.svg?v=2#battery',
            },
            {
              path: '/admin/cabinets',
              name: 'Cabinets',
              icon: 'assets/icons/navigation.svg?v=2#stack',
            },
          ]
        : []),
    ];
  }

  return [
    ...(hasFullAdminOrgAccess
      ? [
          {
            path: '/admin/dashboard',
            name: 'Admin',
            icon: 'assets/icons/navigation.svg?v=2#admin',
          },
          {
            path: '/admin/resellers',
            name: 'Resellers',
            icon: 'assets/icons/navigation.svg?v=2#customers',
          },
        ]
      : []),
    ...(canAccessResellerWorkspace
      ? [
          {
            path: hasAdminProjectsAccess ? '/admin/customer-profiles' : '/customer-profiles',
            name: 'Customer Profiles',
            icon: 'assets/icons/navigation.svg?v=2#contact-card',
          },
        ]
      : []),
    {
      path: hasFullAdminOrgAccess ? '/admin/users' : '/users',
      name: 'Users',
      icon: 'assets/icons/navigation.svg?v=2#users',
    },
    {
      path: hasFullAdminOrgAccess ? '/admin/projects' : '/projects',
      name: 'Projects',
      icon: 'assets/icons/navigation.svg?v=2#projects',
    },
    {
      path: hasAdminProjectsAccess ? '/admin/support' : '/support',
      name: 'Support',
      icon: 'assets/icons/navigation.svg?v=2#support',
    },
    ...(canAccessProductionView
      ? [
          {
            path: '/admin/production',
            name: 'Production',
            icon: 'assets/icons/navigation.svg?v=2#stack',
          },
        ]
      : []),
    ...(canAccessResellerWorkspace && isInAdminOrg
      ? [
          {
            path: hasAdminProjectsAccess && canAccessBatteryCatalogAdmin ? '/admin/batteries' : '/batteries',
            name: 'Batteries',
            icon: 'assets/icons/navigation.svg?v=2#battery',
          },
          {
            path: hasAdminProjectsAccess && canAccessBatteryCatalogAdmin ? '/admin/cabinets' : '/cabinets',
            name: 'Cabinets',
            icon: 'assets/icons/navigation.svg?v=2#stack',
          },
        ]
      : []),
    ...(hasFullAdminOrgAccess
      ? [
          {
            path: '/admin/cables',
            name: 'Cables',
            icon: 'assets/icons/navigation.svg?v=2#stack',
          },
        ]
      : []),
  ];
}

@Component({
    selector: 'dashboard-header-nav',
    templateUrl: './dashboard-header-nav.component.html',
    styleUrls: ['./dashboard-header-nav.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DashboardHeaderNavComponent {
  private readonly authService = inject(AuthService);
  private readonly notificationsService = inject(NotificationsService);

  public readonly activeRequestCount$ = this.notificationsService.activeRequestCount$;
  public readonly viewModel$ = this.authService.user$.pipe(
    map((user): DashboardHeaderNavViewModel => {
      if (user == null) {
        return {
          routes: [],
          notificationsPath: '/notifications',
        };
      }

      const hasAdminProjectsAccess = this.authService.hasAdminProjectsAccessSync;
      const hasFullAdminOrgAccess = this.authService.hasFullAdminOrgAccessSync;

      return {
        routes: buildRoutes({
          authority: user.authority,
          hasAdminProjectsAccess,
          hasFullAdminOrgAccess,
          canAccessBatteryCatalogAdmin: this.authService.canAccessBatteryCatalogAdminSync,
          canAccessProductionView: this.authService.canAccessProductionViewSync,
          canAccessResellerWorkspace: this.authService.canAccessResellerWorkspaceSync,
          isInAdminOrg: this.authService.isInAdminOrgSync,
        }),
        notificationsPath: hasAdminProjectsAccess ? '/admin/notifications' : '/notifications',
      };
    }),
  );
}
